import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { LocaleSelectContext } from '../../layouts/LocaleProvider/LocaleProvider';
import { UploadCSVForm } from '../../features/UploadCSVForm';
import styles from './index.module.scss';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentStore } from '../../store/store/selectors';
import { apiClient } from '../../utils/CLIENT';

const contentTypesToFileTypes = {
  'text/csv': '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
};

export function UploadCSV() {
  const localeContext = useContext(LocaleSelectContext);
  const store = useAppSelector(selectCurrentStore);
  const navigate = useNavigate();
  const intl = useIntl();
  const [file, setFile] = useState<File | null>(null);
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const [canStoreDownloadCsv, setCanStoreDownloadCsv] = useState<boolean>(false);
  const [status, setStatus] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  useEffect(() => {
    if (!store.settings.enableUploadCSV) {
      navigate('/');
    }
  }, [store]);

  useEffect(() => {
    apiClient.get('/upload-employees/can-store-download-latest-csv')
      .then((response) => {
        setCanStoreDownloadCsv(response.data);
      })
      .catch((error) => {
        console.error('Error preloading file:', error);
      });
  }, []);

  const onUpload = async () => {
    if (!file) return;

    try {
      setStatus(null);
      setFileLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      await apiClient.post('/upload-employees/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      setFile(null);

      setStatus({
        message: intl.formatMessage({ id: 'uploadShifts.success' }),
        type: 'success',
      });
    } catch (e: any) {
      const errorData = e.message.split('--');
      if (errorData.length >= 2) {
        setStatus({
          message: intl.formatMessage({ id: errorData[0] }, {
            row: errorData[1],
            field: errorData[2],
          }),
          type: 'error',
        });
      } else {
        setStatus({
          message: intl.formatMessage({ id: 'common.error' }),
          type: 'error',
        });
      }
    } finally {
      setFileLoading(false);
    }
  };

  const onDownload = async () => {
    try {
      const response = await apiClient.get('/upload-employees/download-latest', {
        responseType: 'blob',
      });

      const blob = await response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `latest-shifts${contentTypesToFileTypes[response.data.type]}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localeContext?.locale || 'en'}>
      <Box className={styles.container}>
        <Box className={styles.content}>
          <UploadCSVForm
            status={status}
            fileLoading={fileLoading}
            setFile={setFile}
            onUploadClick={onUpload}
            onDownloadClick={canStoreDownloadCsv ? onDownload : null}
            canFileBeSent={!!file}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
